import {
  BONDS, BRIDGE_LOAN, CASH_ON_HAND, CERTIFICATE_OF_DEPOSIT, CHECKING, CHECKING_SAVINGS,
  DEPOSIT_ON_SALES_CONTRACT, EQUITY_FROM_PENDING_SALE, EQUITY_FROM_SUBJECT_PROPERTY,
  EQUITY_ON_SOLD_PROPERTY, GIFT_EMPLOYER, GIFT_GOVERNMENT_ASSISTANCE,
  GIFT_NON_SELLER_FUNDED_NON_PROFIT, GIFT_RELATIVE, GIFT_SELLER_FUNDED_NON_PROFIT,
  GIFT_SOURCE_N_A, INDIVIDUAL_DEVELOPMENT_ACCOUNT, LIFE_INSURANCE_CASH_VALUE, LOT_EQUITY,
  MONEY_MARKET_FUND, MUTUAL_FUND, OTHER_TYPE_OF_DOWN_PAYMENT, RENT_WITH_OPTION_TO_PURCHASE,
  RETIREMENT_ASSET, RETIREMENT_FUNDS, SALE_OF_CHATTEL, SAVING, SECURED_BORROWED_FUNDS,
  STOCK_OPTIONS, STOCKS, STOCKS_BONDS, SWEAT_EQUITY, TRADE_EQUITY, TRUST_FUNDS,
  UNSECURED_BORROWED_FUNDS, AUTOMOBILE
} from "../../constants/reserves_account_types";
import { PERCENT_CREDITED_AGENCIES } from "../../constants/reserves_percentage_credited";
import Decimal from "decimal.js";
import {
  total_misc_monthly_fees,
  net_income_or_loss,
  wrap_subject_property_if_needed
} from "../../calculations/investment_reo";
import { YES } from "../../constants/yes_no";
import { OWN } from "../../constants/renting_or_owning";
import { REFINANCE } from "../../constants/document_types";
import { property_tax } from "../../calculations/loan";
import {
  home_owners_insurance_monthly,
  hoa_dues_1,
  hoa_dues_2,
  present_housing_escrow_monthly
} from "../../calculations/property";
import {
  DISPOSITION_TO_MISMO,
  OCCUPANCY_TO_MISMO
} from "../../constants/property_disposition";

export function getReoAssetLabel(reoIndex) {
  return `ASSET_OWNED_${getReoSeqNum(reoIndex)}`;
}

export function getResidenceAssetLabel(borrowerIndex) {
  return `ASSET_OWNED_RES_${borrowerIndex}`;
}

function residenceAddressKey(borrower) {
  return [
    borrower.current_address,
    borrower.current_address_2,
    borrower.city,
    borrower.state,
    borrower.zip_code
  ]
    .map(v => String(v || "").trim().toLowerCase())
    .join("|");
}

/**
 * Owned current residences to export, deduped by address.
 *
 * Borrowers who share a home (a married couple, or any of the up-to-4 borrowers
 * at the same address) must produce ONE owned-property asset, not one each —
 * otherwise the same house is listed multiple times in the file. The first
 * borrower at an address owns the asset; the others resolve their arcs to it.
 */
export function getExportableResidences(document) {
  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  const seen = {};
  const out = [];
  borrowers.forEach((borrower, borrowerIndex) => {
    if (!residenceNeedsOwnedProperty(document, borrower, borrowerIndex)) return;
    const key = residenceAddressKey(borrower);
    if (seen[key] !== undefined) return;
    seen[key] = borrowerIndex;
    out.push({ borrower, borrowerIndex, label: getResidenceAssetLabel(borrowerIndex) });
  });
  return out;
}

/**
 * The residence asset label a given borrower's mortgage should point at —
 * the canonical (first) borrower sharing that address.
 */
export function getResidenceAssetLabelFor(document, borrowerIndex) {
  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  const borrower = borrowers[borrowerIndex];
  if (!borrower) return getResidenceAssetLabel(borrowerIndex);
  const key = residenceAddressKey(borrower);
  const match = getExportableResidences(document).find(
    r => residenceAddressKey(r.borrower) === key
  );
  return match ? match.label : getResidenceAssetLabel(borrowerIndex);
}

// A current residence is written as an OWNED_PROPERTY asset (in addition to the
// RESIDENCE, which DU still reads for history) only when it is OWNED and either
// the LO set a status/occupancy on it or a mortgage is attached to it — so we
// don't add owned-property assets gratuitously.
export function residenceNeedsOwnedProperty(document, borrower, borrowerIndex) {
  if (!borrower || borrower.currently_renting_or_owning !== OWN) return false;
  if (!borrower.current_address) return false;
  if (
    borrower.residence_disposition_status ||
    borrower.residence_intended_occupancy ||
    borrower.residence_current_occupancy
  ) {
    return true;
  }
  const key = `res:${borrowerIndex}`;
  return Object.values(document.accountSummaries || {}).some(
    a => Array.isArray(a.attached_properties) && a.attached_properties.includes(key)
  );
}

const ASSET_TYPE_MISMO = {
  [AUTOMOBILE]: "Automobile",
  [BONDS]: "Bond",
  [BRIDGE_LOAN]: "BridgeLoanNotDeposited",
  [CASH_ON_HAND]: "CashOnHand",
  [CERTIFICATE_OF_DEPOSIT]: "CertificateOfDepositTimeDeposit",
  [CHECKING]: "CheckingAccount",
  [CHECKING_SAVINGS]: "CheckingAccount",
  [DEPOSIT_ON_SALES_CONTRACT]: "EarnestMoneyCashDepositPaidBySeller",
  [EQUITY_FROM_PENDING_SALE]: "ProceedsFromSaleOfNonRealEstateAsset",
  [EQUITY_FROM_SUBJECT_PROPERTY]: "EquityFromSubjectProperty",
  [EQUITY_ON_SOLD_PROPERTY]: "ProceedsFromRealEstateSold",
  [GIFT_EMPLOYER]: "GiftFundsEmployer",
  [GIFT_GOVERNMENT_ASSISTANCE]: "GiftFundsGovernmentalAgency",
  [GIFT_NON_SELLER_FUNDED_NON_PROFIT]: "GiftFundsNonProfitOrganization",
  [GIFT_RELATIVE]: "GiftFundsFamilyMember",
  [GIFT_SELLER_FUNDED_NON_PROFIT]: "GiftFundsNonProfitOrganization",
  [GIFT_SOURCE_N_A]: "Other",
  [INDIVIDUAL_DEVELOPMENT_ACCOUNT]: "IndividualDevelopmentAccount",
  [LIFE_INSURANCE_CASH_VALUE]: "CashValueOfLifeInsurance",
  [LOT_EQUITY]: "LotEquity",
  [MONEY_MARKET_FUND]: "MoneyMarketFund",
  [MUTUAL_FUND]: "MutualFund",
  [OTHER_TYPE_OF_DOWN_PAYMENT]: "Other",
  [RENT_WITH_OPTION_TO_PURCHASE]: "RentWithOptionToPurchase",
  [RETIREMENT_ASSET]: "RetirementFund",
  [RETIREMENT_FUNDS]: "RetirementFund",
  [SALE_OF_CHATTEL]: "ProceedsFromSaleOfNonRealEstateAsset",
  [SAVING]: "SavingsAccount",
  [SECURED_BORROWED_FUNDS]: "SecuredBorrowedFunds",
  [STOCK_OPTIONS]: "StockOptions",
  [STOCKS]: "Stock",
  [STOCKS_BONDS]: "Stock",
  [SWEAT_EQUITY]: "SweatEquity",
  [TRADE_EQUITY]: "TradeEquity",
  [TRUST_FUNDS]: "TrustFunds",
  [UNSECURED_BORROWED_FUNDS]: "UnsecuredBorrowedFunds"
};

export function getFinancialAssetRecords(document) {
  const results = [];
  const borrowers = (document.applicant && document.applicant.borrowers) || [];
  borrowers.forEach((borrower, borrowerIndex) => {
    (borrower.reserves || []).forEach(reserve => {
      if (parseFloat(reserve.funds) > 0) {
        results.push({ ...reserve, borrowerIndex });
      }
    });
  });
  return results;
}

export function getAssetSeqNum(globalAssetIndex) {
  return 100 + (globalAssetIndex + 1) * 10 + 1;
}

export function getReoSeqNum(reoIndex) {
  return 1111 + reoIndex;
}

export function populateDealAssets(builder, { document, options }) {
  const documentType = options && options.documentType;
  const allAssets = getFinancialAssetRecords(document);

  // For a refinance, the borrower's current residence IS the subject property, so
  // its owned-property record needs the subject's escrow (Tax + Insurance + HOA)
  // and current-lien UPB — POINT reads these into the Section 3a "Ins/Tax/Assoc"
  // and unpaid-balance fields. The escrow comes from the subject `property`
  // refinance fields (same values as the Current housing rows), NOT the
  // property_to_be_sold.present_* fields (those are the Purchase departing home).
  let refiSubjectEscrow = new Decimal(0);
  let refiSubjectLienUpb = new Decimal(0);
  if (documentType === REFINANCE) {
    const selIndex =
      document.summary && document.summary.selection_loan_number
        ? parseInt(document.summary.selection_loan_number, 10) - 1
        : 0;
    const selLoan =
      (document.loans && (document.loans[selIndex] || document.loans[0])) || {};
    const prop = document.property || {};
    refiSubjectEscrow = property_tax(REFINANCE, selLoan, prop)
      .add(home_owners_insurance_monthly(REFINANCE, prop))
      .add(hoa_dues_1(prop, selLoan))
      .add(hoa_dues_2(prop, selLoan));
    const pts = document.property_to_be_sold || {};
    refiSubjectLienUpb = new Decimal(pts.first_loan_balance || 0)
      .add(pts.second_loan_balance || 0)
      .add(pts.third_loan_balance || 0);
  }

  // For a PURCHASE where the borrower's current primary residence is being sold,
  // that departing home's escrow (Ins/Tax/Assoc) and lien UPB live on the Selling
  // Property tab (`property_to_be_sold.present_*` + loan balances) — a DIFFERENT
  // source than the Refinance subject fields. POINT reads these into Section 3a's
  // "Ins/Tax/Assoc" and unpaid-balance fields for the pending-sale residence.
  // Ins/Tax/Assoc = monthly (Tax + Insurance + HOA1 + HOA2 + Mello Roos/Other).
  let purchaseDepartingEscrow = new Decimal(0);
  let purchaseDepartingLienUpb = new Decimal(0);
  if (documentType !== REFINANCE && document.is_selling_property === YES) {
    const pts = document.property_to_be_sold || {};
    purchaseDepartingEscrow = present_housing_escrow_monthly(pts);
    purchaseDepartingLienUpb = new Decimal(pts.first_loan_balance || 0)
      .add(pts.second_loan_balance || 0)
      .add(pts.third_loan_balance || 0);
  }

  allAssets.forEach((reserve, globalAssetIndex) => {
    const seqNum = getAssetSeqNum(globalAssetIndex);
    const ele = builder.ele("ASSET");
    ele.att("SequenceNumber", String(seqNum));
    ele.att("xlink:label", `ASSET_${seqNum}`);

    const detail = ele.ele("ASSET_DETAIL");
    if (reserve.account_number) {
      detail.ele("AssetAccountIdentifier").txt(reserve.account_number);
    }
    const pct = parseFloat(PERCENT_CREDITED_AGENCIES[reserve.sub_type] || "100") / 100;
    const amount = new Decimal(parseFloat(reserve.funds) || 0).times(pct);
    detail.ele("AssetCashOrMarketValueAmount").txt(amount.toDecimalPlaces(2).toString());
    const assetType = ASSET_TYPE_MISMO[reserve.sub_type] || "Other";
    detail.ele("AssetType").txt(assetType);

    if (reserve.bank_name) {
      ele.ele("ASSET_HOLDER").ele("NAME").ele("FullName").txt(reserve.bank_name);
    }
  });

  const properties = document.investment_properties || [];
  properties.forEach((ip, reoIndex) => {
    // Guard against double-listing the borrower's PRIMARY residence. On a
    // refinance, a retained primary residence is the subject home (exported as a
    // current OWNED residence + property detail), NOT an investment REO. If it was
    // also entered on the Invest REO page (owner-occupied primary), skip it here so
    // POINT's Section 3a doesn't show the home twice. Real REO (investment / second
    // home occupancy) is unaffected.
    const reoOccupancy =
      OCCUPANCY_TO_MISMO[ip.intended_occupancy] ||
      OCCUPANCY_TO_MISMO[ip.current_occupancy];
    if (reoOccupancy === "PrimaryResidence") return;

    const seqNum = getReoSeqNum(reoIndex);
    const ele = builder.ele("ASSET");
    ele.att("SequenceNumber", String(seqNum));
    ele.att("xlink:label", `ASSET_OWNED_${seqNum}`);

    const ownedProp = ele.ele("OWNED_PROPERTY");
    const detail = ownedProp.ele("OWNED_PROPERTY_DETAIL");
    detail.ele("OwnedPropertyDispositionStatusType").txt(
      DISPOSITION_TO_MISMO[ip.disposition_status] || "Retain"
    );

    const upb = parseFloat(ip.principal_balance) || 0;
    detail.ele("OwnedPropertyLienUPBAmount").txt(String(upb));

    const maintenance = total_misc_monthly_fees(ip);
    detail.ele("OwnedPropertyMaintenanceExpenseAmount").txt(
      maintenance.toDecimalPlaces(2).toString()
    );

    const grossRent = parseFloat(ip.gross_rent_payment) || 0;
    if (grossRent > 0) {
      detail.ele("OwnedPropertyRentalIncomeGrossAmount").txt(String(grossRent));
      // NOTE: OwnedPropertyRentalIncomeNetAmount is intentionally NOT exported.
      // POINT reads the borrower's "Net Rent" from that DEAL-level field and then
      // COPIES it onto every borrower pair in a multi-applicant file, regardless
      // of who owns the property (double-counting the loan income). Instead the
      // REO Schedule E net is exported as a borrower-scoped 1e income item (see
      // mismo_config CURRENT_INCOME_ITEM forEach), which POINT keeps on the
      // owning borrower only.
    }

    const isSubject = ip.is_subject_property === YES;
    detail.ele("OwnedPropertySubjectIndicator").txt(String(isSubject));

    const property = ownedProp.ele("PROPERTY");
    const addr = property.ele("ADDRESS");
    if (ip.property_address) addr.ele("AddressLineText").txt(ip.property_address);
    if (ip.property_address_2) addr.ele("AddressAdditionalLineText").txt(ip.property_address_2);
    if (ip.property_city) addr.ele("CityName").txt(ip.property_city);
    addr.ele("CountryCode").txt("US");
    if (ip.property_zip) addr.ele("PostalCode").txt(ip.property_zip);
    if (ip.property_state) addr.ele("StateCode").txt(ip.property_state);

    const propDetail = property.ele("PROPERTY_DETAIL");
    propDetail.ele("PropertyCurrentUsageType").txt(
      OCCUPANCY_TO_MISMO[ip.current_occupancy] || "Investment"
    );
    const marketValue = parseFloat(ip.market_value) || 0;
    if (marketValue > 0) {
      propDetail.ele("PropertyEstimatedValueAmount").txt(String(marketValue));
    }
    propDetail.ele("PropertyUsageType").txt(
      OCCUPANCY_TO_MISMO[ip.intended_occupancy] || "Investment"
    );
  });

  // Current OWNED residences as OWNED_PROPERTY assets (so a mortgage can link
  // to them and their status/occupancy round-trips). Additive to RESIDENCE.
  // Deduped by address so borrowers sharing a home produce ONE asset.
  getExportableResidences(document).forEach(({ borrower, borrowerIndex, label }) => {
    const ele = builder.ele("ASSET");
    ele.att("SequenceNumber", String(9100 + borrowerIndex));
    ele.att("xlink:label", label);

    const ownedProp = ele.ele("OWNED_PROPERTY");
    const detail = ownedProp.ele("OWNED_PROPERTY_DETAIL");
    detail.ele("OwnedPropertyDispositionStatusType").txt(
      DISPOSITION_TO_MISMO[borrower.residence_disposition_status] || "Retain"
    );
    // Refinance subject residence: carry the current lien UPB + escrow so POINT's
    // Section 3a "Ins/Tax/Assoc" (Tax + Insurance + HOA) populates. (XSD order:
    // LienUPB and MaintenanceExpense before SubjectIndicator.)
    if (documentType === REFINANCE) {
      detail
        .ele("OwnedPropertyLienUPBAmount")
        .txt(refiSubjectLienUpb.toDecimalPlaces(2).toString());
      if (refiSubjectEscrow.gt(0)) {
        detail
          .ele("OwnedPropertyMaintenanceExpenseAmount")
          .txt(refiSubjectEscrow.toDecimalPlaces(2).toString());
      }
    } else if (
      DISPOSITION_TO_MISMO[borrower.residence_disposition_status] === "PendingSale"
    ) {
      // Purchase: departing primary being sold. Carry the Selling Property tab's
      // lien UPB + escrow so POINT's Section 3a "Ins/Tax/Assoc" and unpaid balance
      // populate (XSD order: LienUPB and MaintenanceExpense before SubjectIndicator).
      if (purchaseDepartingLienUpb.gt(0)) {
        detail
          .ele("OwnedPropertyLienUPBAmount")
          .txt(purchaseDepartingLienUpb.toDecimalPlaces(2).toString());
      }
      if (purchaseDepartingEscrow.gt(0)) {
        detail
          .ele("OwnedPropertyMaintenanceExpenseAmount")
          .txt(purchaseDepartingEscrow.toDecimalPlaces(2).toString());
      }
    }
    detail.ele("OwnedPropertySubjectIndicator").txt("false");

    const property = ownedProp.ele("PROPERTY");
    const addr = property.ele("ADDRESS");
    if (borrower.current_address) addr.ele("AddressLineText").txt(borrower.current_address);
    if (borrower.current_address_2) addr.ele("AddressAdditionalLineText").txt(borrower.current_address_2);
    if (borrower.city) addr.ele("CityName").txt(borrower.city);
    addr.ele("CountryCode").txt("US");
    if (borrower.zip_code) addr.ele("PostalCode").txt(borrower.zip_code);
    if (borrower.state) addr.ele("StateCode").txt(borrower.state);

    const propDetail = property.ele("PROPERTY_DETAIL");
    propDetail.ele("PropertyCurrentUsageType").txt(
      OCCUPANCY_TO_MISMO[borrower.residence_current_occupancy] || "PrimaryResidence"
    );
    const marketValue = parseFloat(borrower.market_value) || 0;
    if (marketValue > 0) {
      propDetail.ele("PropertyEstimatedValueAmount").txt(String(marketValue));
    }
    propDetail.ele("PropertyUsageType").txt(
      OCCUPANCY_TO_MISMO[borrower.residence_intended_occupancy] || "PrimaryResidence"
    );
  });
}
